import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

function PrivacyPolicyMain() {
    const { setCurrentPage } = useOutletContext();

    useEffect(() => {
        setCurrentPage("Privacy Policy");
    }, [setCurrentPage]);
    
    const sections = [
        { id: 'introduction', label: 'Introduction', icon: 'info' },
        { id: 'data-collection', label: 'Data Collection', icon: 'database' },
        { id: 'game-data', label: 'Game Data & Telemetry', icon: 'monitoring' },
        { id: 'steam-integration', label: 'Steam Integration', icon: 'sports_esports' },
        { id: 'third-party', label: 'Third-Party Services', icon: 'cloud_sync' },
        { id: 'user-rights', label: 'User Rights', icon: 'verified_user' },
    ];

    const [activeSection, setActiveSection] = useState(sections[0].id);

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '-20% 0px -70% 0px',
            threshold: 0
        };

        const observerCallback = (entries) => {
            entries.forEach((entry) => {
            if (entry.isIntersecting) {
                setActiveSection(entry.target.id);
            }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        sections.forEach((section) => {
            const element = document.getElementById(section.id);
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, []);

    const scrollToSection = (id) => {
        const element = document.getElementById(id);

        if (element) {
            const offset = 80;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <main className="relative flex min-h-[calc(100vh-64px)] w-full carbon-bg">
            <div className="layout-container max-w-[1440px] mx-auto flex w-full">
                <aside className="hidden lg:flex flex-col w-80 border-r border-border-color p-8 sticky top-16 h-full">
                    <div className="flex flex-col gap-6 sticky top-[140px]">
                        <div className="mb-8">
                            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-4">Policy Contents</h3>
                            {sections.map((section) => {
                                const isActive = activeSection === section.id;
                                
                                return (
                                <button
                                    key={section.id}
                                    onClick={() => scrollToSection(section.id)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all border ${
                                    isActive
                                        ? "bg-primary/10 text-primary border-primary/20 font-bold"
                                        : "hover:bg-surface text-slate-400 hover:text-white border-transparent"
                                    }`}
                                >
                                    <span className="material-symbols-outlined text-xl">
                                    {section.icon}
                                    </span>
                                    <span className="text-sm">
                                    {section.label}
                                    </span>
                                </button>
                                );
                            })}
                        </div>

                        <div className="mt-auto p-4 rounded-xl bg-surface/50 border border-border-color">
                            <p className="text-xs text-slate-400 leading-relaxed mb-3">Need help with your data or privacy settings?</p>
                            <button className="w-full py-2 bg-surface hover:bg-border-color border border-border-color text-xs font-bold rounded-lg transition-colors">Contact Support</button>
                        </div>
                    </div>
                </aside>

                <div className="flex-1 overflow-y-auto content-scroll">
                    <div className="max-w-[850px] mx-auto px-6 py-12 lg:px-12 lg:py-16">
                        <div className="mb-12 border-b border-border-color pb-8">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold uppercase tracking-wider rounded-full border border-primary/30">Legal</span>
                                <span className="text-slate-500 text-sm">•</span>
                                <span className="text-slate-500 text-sm">Last Updated: February 09, 2026</span>
                            </div>
                            <h1 className="text-5xl font-black text-white tracking-tight mb-4 leading-tight">Privacy Policy</h1>
                            <p className="text-slate-400 text-lg leading-relaxed max-w-2xl">
                                At ACCLiveTelemetry, we are committed to protecting your privacy while providing the best real-time racing data analysis for Assetto Corsa Competizione.
                            </p>
                        </div>

                        <section className="mb-16 scroll-mt-24" id="introduction">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">1. Introduction</h2>
                            </div>
                            <div className="space-y-4 text-slate-300 leading-relaxed">
                                <p>This Privacy Policy describes how ACCLiveTelemetry ("we," "us," or "our") collects, uses, and shares your personal information when you use our platform, including our website and telemetry client software.</p>
                                <p>By using ACCLiveTelemetry, you agree to the collection and use of information in accordance with this policy. We prioritize transparency, especially concerning how your racing performance data is handled and shared with the community.</p>
                            </div>
                        </section>

                        <section className="mb-16 scroll-mt-24" id="data-collection">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">2. Data Collection</h2>
                            </div>
                            <div className="bg-surface rounded-xl p-6 border border-border-color mb-6">
                                <h3 className="text-primary font-bold mb-3 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-sm">person</span>
                                    Account Information
                                </h3>
                                <p className="text-slate-300 mb-4">When you register or log in via Steam, we collect your Steam ID64, display name, and avatar. This is used to link your racing sessions to your unique profile.</p>
                                <ul className="list-disc pl-5 space-y-2 text-slate-400 text-sm">
                                    <li>Public Steam Profile data (ID, Name, Avatar)</li>
                                    <li>Email address (optional, for notifications)</li>
                                    <li>User-defined privacy preferences</li>
                                </ul>
                            </div>
                        </section>

                        <section className="mb-16 scroll-mt-24" id="game-data">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">3. Game Data &amp; Telemetry</h2>
                            </div>
                            <div className="space-y-6">
                                <p className="text-slate-300">The core of our service involves processing real-time UDP data from your Assetto Corsa Competizione (ACC) client. This data includes:</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-surface/40 p-5 rounded-xl border border-border-color/50">
                                        <div className="text-white font-bold mb-2 flex items-center gap-2">
                                            <span className="material-symbols-outlined text-primary">timer</span>
                                            Performance Data
                                        </div>
                                        <p className="text-sm text-slate-400">Lap times, sector times, split times, and overall consistency metrics.</p>
                                    </div>
                                    <div className="bg-surface/40 p-5 rounded-xl border border-border-color/50">
                                        <div className="text-white font-bold mb-2 flex items-center gap-2">
                                            <span className="material-symbols-outlined text-primary">tire_repair</span>
                                            Vehicle Telemetry
                                        </div>
                                        <p className="text-sm text-slate-400">Tire pressure, temperatures, brake wear, fuel usage, and damage states.</p>
                                    </div>
                                    <div className="bg-surface/40 p-5 rounded-xl border border-border-color/50">
                                        <div className="text-white font-bold mb-2 flex items-center gap-2">
                                            <span className="material-symbols-outlined text-primary">location_on</span>
                                            Positioning
                                        </div>
                                        <p className="text-sm text-slate-400">Real-time track position, velocity, and G-force vectors.</p>
                                    </div>
                                    <div className="bg-surface/40 p-5 rounded-xl border border-border-color/50">
                                        <div className="text-white font-bold mb-2 flex items-center gap-2">
                                            <span className="material-symbols-outlined text-primary">share</span>
                                            Live Sharing
                                        </div>
                                        <p className="text-sm text-slate-400">Optionally share live sessions with friends via unique 'Live Links'.</p>
                                    </div>
                                </div>
                                <div className="bg-primary/10 border-l-4 border-primary p-6 rounded-r-xl">
                                    <h4 className="text-white font-bold mb-2">How Live Telemetry is Shared</h4>
                                    <p className="text-sm text-slate-300">By default, your live session data is private. If you enable "Public Streaming" or share your "Live Token," users with that link can view your real-time dashboard. Local client data is encrypted during transit via SSL/TLS.</p>
                                </div>
                            </div>
                        </section>

                        <section className="mb-16 scroll-mt-24" id="steam-integration">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">4. Steam Integration</h2>
                            </div>
                            <div className="prose prose-invert prose-slate max-w-none text-slate-300">
                                <p>We use the Steam Web API to verify your identity. We do not have access to your Steam login credentials (username/password). The authentication process is handled entirely by Valve's servers, which return a unique identifier to us upon successful login.</p>
                                <div className="flex items-center gap-4 bg-surface p-4 rounded-xl border border-border-color mt-4">
                                    <span className="material-symbols-outlined text-4xl text-slate-500">api</span>
                                    <p className="text-sm text-slate-400">Our integration follows the OpenID protocol standards. Your Steam wallet, games library (beyond ACC ownership), and friends list are not modified or accessed for writing.</p>
                                </div>
                            </div>
                        </section>

                        <section className="mb-16 scroll-mt-24" id="third-party">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">5. Third-Party Services</h2>
                            </div>
                            <p className="text-slate-300 mb-6">We may employ third-party companies and individuals due to the following reasons:</p>
                            <div className="space-y-4">
                                <div className="flex gap-4 items-start">
                                    <span className="size-6 rounded-full bg-border-color flex items-center justify-center text-xs font-bold text-white shrink-0 mt-1">1</span>
                                    <p className="text-slate-400"><strong className="text-white">Cloud Hosting:</strong> We use OVH (Not affiliated) for server infrastructure and content delivery. Your data resides on secure servers located in the EU and North America.</p>
                                </div>
                                <div className="flex gap-4 items-start">
                                    <span className="size-6 rounded-full bg-border-color flex items-center justify-center text-xs font-bold text-white shrink-0 mt-1">2</span>
                                    <p className="text-slate-400"><strong className="text-white">Analytics:</strong> We use simplified, privacy-first analytics to understand aggregate site traffic. We do not use intrusive tracking cookies for advertising.</p>
                                </div>
                            </div>
                        </section>

                        <section className="mb-16 scroll-mt-24" id="user-rights">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">6. User Rights</h2>
                            </div>
                            <p className="text-slate-300 mb-6">You have the right to access, update, or delete the information we have on you. Within your profile settings, you can:</p>
                            <div className="bg-surface border border-border-color rounded-xl divide-y divide-border-color">
                                <div className="p-4 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="material-symbols-outlined text-slate-500">delete_sweep</span>
                                        <span className="text-sm font-medium">Clear Session History</span>
                                    </div>
                                    <button className="text-xs text-primary font-bold hover:underline">Manage</button>
                                </div>
                                <div className="p-4 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="material-symbols-outlined text-slate-500">no_accounts</span>
                                        <span className="text-sm font-medium">Permanent Account Deletion</span>
                                    </div>
                                    <button className="text-xs text-red-500 font-bold hover:underline">Delete</button>
                                </div>
                            </div>
                        </section>

                        <div className="mt-20 p-10 rounded-2xl bg-gradient-to-br from-surface to-background-dark border border-border-color text-center relative overflow-hidden">
                            <div className="absolute -right-10 -top-10 text-primary/10 opacity-20 rotate-12">
                                <span className="material-symbols-outlined" style={{fontSize: "200px"}}>gavel</span>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-4">Questions about our legal terms?</h3>
                            <p className="text-slate-400 mb-8 max-w-md mx-auto">Our legal team is happy to answer any questions you have regarding data safety and platform compliance.</p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <button className="bg-primary hover:bg-primary/90 text-white font-bold py-3 px-8 rounded-lg transition-all shadow-lg shadow-primary/20">Email Legal Team</button>
                                <button className="bg-surface hover:bg-border-color text-white border border-border-color font-bold py-3 px-8 rounded-lg transition-all">Download PDF Version</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}

export default PrivacyPolicyMain;